import { useScrollReveal } from "@/hooks/useScrollReveal";
import CursorGlow from "@/components/landing/CursorGlow";

const PILLARS = [
  {
    id: "visibility",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
    eyebrow: "Visibility",
    title: "Always-On Market Presence",
    copy: "We architect omnipresent visibility across paid, organic, and offline channels so your category leadership becomes the default consumer assumption — not an aspiration.",
  },
  {
    id: "performance",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80",
    eyebrow: "Performance",
    title: "Revenue-Linked Campaigns",
    copy: "Every rupee deployed is reconciled against pipeline, qualified leads, and bookings. Reporting is audit-grade — built for CFOs, not vanity dashboards.",
  },
  {
    id: "results",
    image:
      "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1200&q=80",
    eyebrow: "Results",
    title: "Compounding Brand Equity",
    copy: "Creative, narrative, and media work in lock-step so each campaign cycle multiplies the next. Short-term spikes become long-term moats.",
  },
];

function PillarCard({ p, idx }) {
  const { ref } = useScrollReveal({ threshold: 0.2 });
  return (
    <article
      ref={ref}
      data-testid={`visibility-card-${p.id}`}
      style={{ transitionDelay: `${idx * 140}ms` }}
      className="reveal-up group relative rounded-2xl overflow-hidden bg-white border border-black/10 transition-all duration-500 hover:-translate-y-2 hover:border-[#2770FD] hover:shadow-[0_20px_50px_-15px_rgba(39,112,253,0.35)]"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-black/5">
        <img
          src={p.image}
          alt={`${p.title} — Dynamic X Media ${p.eyebrow.toLowerCase()} pillar visual for high-growth real estate and modern brands`}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
        />
        <span className="absolute top-4 left-4 inline-flex items-center px-3 py-1 rounded-full bg-white/95 backdrop-blur border border-black/10 text-[10px] uppercase tracking-[0.25em] text-[#2770FD] font-semibold">
          {p.eyebrow}
        </span>
      </div>
      <div className="p-7 sm:p-8">
        <h3 className="font-display text-black text-2xl leading-tight">
          {p.title}
        </h3>
        <p className="mt-3 text-black/70 text-sm sm:text-base font-light leading-relaxed">
          {p.copy}
        </p>
      </div>
    </article>
  );
}

export default function Visibility() {
  return (
    <section
      id="visibility"
      data-testid="visibility-section"
      className="relative py-24 sm:py-32 bg-white overflow-hidden"
    >
      <CursorGlow size={650} />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto">
          <span className="inline-block text-xs uppercase tracking-[0.3em] font-semibold text-[#2770FD] mb-5">
            Why Brands Choose Us
          </span>
          <h2
            data-testid="visibility-heading"
            className="font-display text-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            Designed for Visibility,{" "}
            <span className="text-accent-blue">Built for Results.</span>
          </h2>
          <p className="mt-6 text-black/70 text-base sm:text-lg font-light leading-relaxed">
            Three non-negotiable operating outcomes baked into every Dynamic X Media engagement.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {PILLARS.map((p, idx) => (
            <PillarCard key={p.id} p={p} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}
